import {
  ActivityStatus,
  dashboardService,
  TimeGranularity
} from '@/services/dashboard.service';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Award,
  BookOpen,
  Building,
  Calendar,
  CalendarClock,
  Coins,
  Loader2,
  RefreshCw,
  Sparkles,
  Target,
  Users,
  Wallet
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useSearchParams } from 'react-router-dom';
import {
  usePageTracking,
  useTimeTracking,
  useScrollTracking,
} from '@/hooks/useAnalytics';
import { trackEvent } from '@/utils/analytics';
import { trackCount, trackGauge, trackDistribution } from '@/utils/sentry';
import { ADMIN_DASHBOARD_EVENTS } from '@/constants/analytics.events';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import { DASHBOARD_PERMISSION } from '@/constants/constant-permissions';
import { ADMIN_REPORTS_ACTIVITY } from '@/constants/constant-routes';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis, YAxis
} from 'recharts';

// Reusable component for showing an empty chart state
const EmptyChartState = ({ message }: { message: string }) => (
  <div className="flex justify-center items-center h-full min-h-[300px] text-center">
    <div className="text-gray-500">
      <p className="font-semibold">No Data Available</p>
      <p className="text-sm">{message}</p>
    </div>
  </div>
);

export default function Dashboard() {
  // Permission guard — redirect to /access-denied if user lacks dashboard_read
  usePermissionRouteAccess([DASHBOARD_PERMISSION.DASHBOARD_READ]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Analytics hooks
  usePageTracking('/dashboard');
  useTimeTracking('/dashboard');
  useScrollTracking('/dashboard');

  // Sentry metrics — sample usage
  useEffect(() => {
    trackCount('page_view', 1, { page: 'dashboard' });

    const perfEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (perfEntry) {
      const loadTime = Math.round(perfEntry.loadEventEnd - perfEntry.fetchStart);
      trackGauge('page_load_time', loadTime, { page: 'dashboard' });
    }
  }, []);

  // Initialize state from URL params or defaults
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year' | 'custom'>(
    (searchParams.get('granularity') as 'week' | 'month' | 'quarter' | 'year' | 'custom') || 'month'
  );
  const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');

  // Update URL params when filters change
  const updateSearchParams = (
    granularity: string,
    start?: string,
    end?: string
  ) => {
    const params = new URLSearchParams();
    params.set('granularity', granularity);

    if (granularity === 'custom' && start && end) {
      params.set('start_date', start);
      params.set('end_date', end);
    }

    setSearchParams(params);
  };

  // Handle time range change
  const handleTimeRangeChange = (range: 'week' | 'month' | 'quarter' | 'year' | 'custom') => {
    setTimeRange(range);

    // Track filter change
    trackEvent(ADMIN_DASHBOARD_EVENTS.FILTER_TIME_RANGE, {
      filter_value: range,
    });

    if (range === 'custom') {
      // Keep existing dates if available
      updateSearchParams(range, startDate, endDate);
    } else {
      // Clear custom dates for non-custom ranges
      setStartDate('');
      setEndDate('');
      updateSearchParams(range);
    }
  };

  // Handle start date change
  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    if (date && endDate) {
      updateSearchParams('custom', date, endDate);
      // Track custom date range selection
      trackEvent(ADMIN_DASHBOARD_EVENTS.FILTER_CUSTOM_DATE, {
        start_date: date,
        end_date: endDate,
      });
    }
  };

  // Handle end date change
  const handleEndDateChange = (date: string) => {
    setEndDate(date);
    if (startDate && date) {
      updateSearchParams('custom', startDate, date);
      // Track custom date range selection
      trackEvent(ADMIN_DASHBOARD_EVENTS.FILTER_CUSTOM_DATE, {
        start_date: startDate,
        end_date: date,
      });
    }
  };

  // Map timeRange to TimeGranularity enum
  const getGranularity = (range: string): TimeGranularity => {
    switch(range) {
      case 'week': return TimeGranularity.WEEKLY;
      case 'quarter': return TimeGranularity.QUARTERLY;
      case 'year': return TimeGranularity.YEARLY;
      default: return TimeGranularity.MONTHLY;
    }
  };

  // Fetch the tenant's own credit summary (fixed plan window — NOT affected by the time-range filter).
  // No refetchInterval / focus refetch: an expired auto-renew plan resets its window on read, so we
  // only want that to happen on a real page load, never a background poll.
  const { data: creditSummary } = useQuery({
    queryKey: ['credit-summary'],
    queryFn: () => dashboardService.getCreditSummary(),
    retry: 1,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch dashboard statistics
  const { data: dashboardStats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['dashboard-stats', timeRange, startDate, endDate],
    queryFn: async () => {
      const start = Date.now();
      const result = await (timeRange === 'custom' && startDate && endDate
        ? dashboardService.getDashboardStats(TimeGranularity.MONTHLY, true, {
            start_date: startDate,
            end_date: endDate
          })
        : dashboardService.getDashboardStats(getGranularity(timeRange), true));
      trackDistribution('api_response_time', Date.now() - start, { endpoint: 'dashboard_stats' });
      return result;
    },
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: timeRange !== 'custom' || (timeRange === 'custom' && !!startDate && !!endDate),
    placeholderData: (previousData) => previousData
  });
  // Fetch recent activities
  const { data: recentActivitiesResponse, isLoading: activitiesLoading } = useQuery({
    queryKey: ['recent-activities', timeRange, startDate, endDate],
    queryFn: () => {
      if (timeRange === 'custom' && startDate && endDate) {
        return dashboardService.getRecentActivities({
          start_date: startDate,
          end_date: endDate,
          limit: 10
        });
      }
      return dashboardService.getRecentActivities({
        granularity: getGranularity(timeRange),
        limit: 10
      });
    },
    refetchInterval: 60 * 1000,
    retry: 2,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    enabled: timeRange !== 'custom' || (timeRange === 'custom' && !!startDate && !!endDate),
    placeholderData: (previousData) => previousData
  });

  // Fetch activity metrics
  const { data: activityMetrics} = useQuery({
    queryKey: ['activity-metrics', timeRange, startDate, endDate],
    queryFn: () => {
      if (timeRange === 'custom' && startDate && endDate) {
        return dashboardService.getActivityMetrics({
          start_date: startDate,
          end_date: endDate,
          limit: 8
        });
      }
      return dashboardService.getActivityMetrics({
        granularity: getGranularity(timeRange),
        limit: 8
      });
    },
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: timeRange !== 'custom' || (timeRange === 'custom' && !!startDate && !!endDate),
    placeholderData: (previousData) => previousData
  });

  // Fetch assignment status stats for chart
  const { data: assignmentStatusStats, isLoading: assignmentStatusLoading } = useQuery({
    queryKey: ['assignment-status-stats', timeRange, startDate, endDate],
    queryFn: () => {
      if (timeRange === 'custom' && startDate && endDate) {
        return dashboardService.getAssignmentStatusStats({
          start_date: startDate,
          end_date: endDate
        });
      }
      return dashboardService.getAssignmentStatusStats({
        granularity: getGranularity(timeRange)
      });
    },
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: timeRange !== 'custom' || (timeRange === 'custom' && !!startDate && !!endDate),
    placeholderData: (previousData) => previousData
  });

  // Fetch certificate earned stats for chart
  const { data: certificateEarnedStats, isLoading: certificateEarnedLoading } = useQuery({
    queryKey: ['certificate-earned-stats', timeRange, startDate, endDate],
    queryFn: () => {
      if (timeRange === 'custom' && startDate && endDate) {
        return dashboardService.getCertificateEarnedStats({
          start_date: startDate,
          end_date: endDate
        });
      }
      return dashboardService.getCertificateEarnedStats({
        granularity: getGranularity(timeRange)
      });
    },
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: timeRange !== 'custom' || (timeRange === 'custom' && !!startDate && !!endDate),
    placeholderData: (previousData) => previousData
  });

  // When the tenant has a plan, the AI Credits card takes the 4th slot; otherwise the
  // Departments card fills it (original 4-card layout).
  const hasPlan = creditSummary?.has_plan === true;

  // Build stats array from API data
  const stats = dashboardStats ? [
    {
      title: 'Total Employees',
      value: dashboardStats.totalEmployees?.value?.toLocaleString() || '0',
      change: dashboardStats.totalEmployees?.change ? `+${dashboardStats.totalEmployees.change}` : '+0',
      trend: dashboardStats.totalEmployees?.trend || 'stable',
      icon: <Users className="w-5 h-5" />,
      color: 'primary',
      description: 'Active users this period',
    },
    // Departments shown only when the tenant has NO plan; otherwise the AI Credits card takes this slot.
    ...(!hasPlan ? [{
      title: 'Departments',
      value: dashboardStats.departments?.value?.toString() || '0',
      change: dashboardStats.departments?.change ? `+${dashboardStats.departments.change}` : '+0',
      trend: dashboardStats.departments?.trend || 'stable',
      icon: <Building className="w-5 h-5" />,
      color: 'success',
      description: 'Active departments',
    }] : []),
    {
      title: 'Active Courses',
      value: dashboardStats.activeCourses?.value?.toString() || '0',
      change: dashboardStats.activeCourses?.change ? `+${dashboardStats.activeCourses.change}` : '+0',
      trend: dashboardStats.activeCourses?.trend || 'stable',
      icon: <BookOpen className="w-5 h-5" />,
      color: 'warning',
      description: 'Currently assigned',
    },
    {
      title: 'Completion Rate',
      value: `${Math.round(dashboardStats.completionRate?.value || 0)}%`,
      change: dashboardStats.completionRate?.percentageChange ?
        `${dashboardStats.completionRate.percentageChange > 0 ? '+' : ''}${dashboardStats.completionRate.percentageChange}%` :
        '+0%',
      trend: dashboardStats.completionRate?.trend || 'stable',
      icon: <Target className="w-5 h-5" />,
      color: 'info',
      description: 'Organization average',
    },
  ] : [];

  const activityHeatmapData = activityMetrics?.data?.events?.slice(0, 6).map(event => ({
    name: event.event_name,
    count: event.count,
    percentage: event.percentage,
    color: event.color
  })) || [];

  const colorMap: Record<string, string> = {
    primary: 'bg-brand-indigo-100 text-brand-indigo-700',
    success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700',
    info: 'bg-blue-100 text-blue-700',
  };

  // Assignment status chart state
  const [assignmentChartType, setAssignmentChartType] = useState<'courses' | 'certifications' | 'learningPaths'>('courses');

  // Transform assignment status stats into chart data (flat, no previous period)
  const assignmentChartData = (() => {
    if (!assignmentStatusStats) return [];
    const breakdown = assignmentStatusStats[assignmentChartType];
    if (!breakdown?.assigned?.length) return [];

    return breakdown.assigned.map((item, index) => ({
      label: item.label,
      date: item.date,
      not_started: breakdown.not_started[index]?.value || 0,
      in_progress: breakdown.in_progress[index]?.value || 0,
      completed: breakdown.completed[index]?.value || 0,
    }));
  })();

  const assignmentChartLabels: Record<string, string> = {
    courses: 'Courses',
    certifications: 'Certifications',
    learningPaths: 'Learning Paths'
  };

  // Transform certificate earned stats into chart data
  const certificateChartData = (() => {
    if (!certificateEarnedStats) return [];
    const courses = certificateEarnedStats.courses || [];
    const certs = certificateEarnedStats.certifications || [];
    const lps = certificateEarnedStats.learningPaths || [];
    // Use whichever array has entries for labels
    const ref = courses.length ? courses : certs.length ? certs : lps;
    if (!ref.length) return [];

    return ref.map((item, index) => ({
      label: item.label,
      date: item.date,
      courses: courses[index]?.value || 0,
      certifications: certs[index]?.value || 0,
      learningPaths: lps[index]?.value || 0,
    }));
  })();

  // Sync state with URL params on mount and when URL changes
  useEffect(() => {
    const granularity = searchParams.get('granularity') as 'week' | 'month' | 'quarter' | 'year' | 'custom';
    const start = searchParams.get('start_date');
    const end = searchParams.get('end_date');

    if (granularity) {
      setTimeRange(granularity);
    }

    if (granularity === 'custom' && start && end) {
      setStartDate(start);
      setEndDate(end);
    }
  }, [searchParams]);

  useEffect(() => {
    if (statsError) {
      toast.error('Failed to load dashboard statistics');
      // Track error
      trackEvent(ADMIN_DASHBOARD_EVENTS.ERROR_STATS_LOAD_FAILED, {
        error_message: 'Failed to load dashboard statistics',
      });
    }
  }, [statsError]);

  // Track dashboard widgets on mount (empty dependency array - fires once)
  useEffect(() => {
    trackEvent(ADMIN_DASHBOARD_EVENTS.WIDGET_STATS_CARDS_VIEW);
    trackEvent(ADMIN_DASHBOARD_EVENTS.WIDGET_DEPT_PERFORMANCE_VIEW);
    trackEvent(ADMIN_DASHBOARD_EVENTS.WIDGET_SKILLS_RADAR_VIEW);
    trackEvent(ADMIN_DASHBOARD_EVENTS.WIDGET_ACTIVITY_HEATMAP_VIEW);
    trackEvent(ADMIN_DASHBOARD_EVENTS.WIDGET_ACTIVITY_TIMELINE_VIEW);
  }, []);

  return (
    <div className="space-y-6">
      {/* Modern Animated Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-brand-indigo-600 via-brand-purple-600 to-brand-pink-500 rounded-2xl p-6 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-white opacity-10 rounded-full blur-3xl"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-8 h-8 text-yellow-300 animate-pulse" />
                <h1 className="text-2xl lg:text-3xl font-bold">
                  Learning Intelligence Hub
                </h1>
              </div>
              <p className="text-brand-indigo-100 max-w-2xl">
                Real-time insights into your organization's learning ecosystem. Track progress, identify trends, and drive continuous improvement.!
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {/* Time Range Buttons Row */}
            <div className="flex flex-wrap gap-2">
              {['week', 'month', 'quarter', 'year', 'custom'].map((range) => (
                <button
                  key={range}
                  onClick={() => handleTimeRangeChange(range as any)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold capitalize transition-all duration-300 transform hover:scale-105 ${
                    timeRange === range
                      ? 'bg-white text-brand-indigo-600 shadow-xl scale-105'
                      : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                  }`}
                >
                  {range === 'custom' && <Calendar className="w-4 h-4 inline mr-1" />}
                  {range}
                </button>
              ))}
            </div>

            {/* Custom Date Range - Inline Expansion */}
            {timeRange === 'custom' && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold text-white">From:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    max={endDate || undefined}
                    className="px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border-0 focus:ring-2 focus:ring-white/50 outline-none shadow-lg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold text-white">To:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    min={startDate || undefined}
                    className="px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border-0 focus:ring-2 focus:ring-white/50 outline-none shadow-lg"
                  />
                </div>
                {startDate && endDate && (
                  <span className="text-sm font-medium text-white drop-shadow px-3 py-2 bg-white/20 rounded-lg">
                    {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animated Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg animate-pulse">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                <div className="w-16 h-4 bg-gray-200 rounded"></div>
              </div>
              <div>
                <div className="w-24 h-8 bg-gray-200 rounded mb-2"></div>
                <div className="w-28 h-4 bg-gray-200 rounded mb-1"></div>
                <div className="w-36 h-3 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))
        ) : (
          stats.map((stat, index) => (
            <div 
              key={index} 
              className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-brand-indigo-500/10 to-brand-purple-500/10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-xl ${colorMap[stat.color]} group-hover:scale-110 transition-transform duration-300`}>
                    {stat.icon}
                  </div>
                  <span className={`flex items-center gap-1 text-sm font-bold px-2 py-1 rounded-full ${
                    stat.trend === 'up' ? 'bg-green-100 text-green-600' : 
                    stat.trend === 'down' ? 'bg-red-100 text-red-600' : 
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {stat.change}
                    {stat.trend === 'up' && <ArrowUp className="w-3 h-3" />}
                    {stat.trend === 'down' && <ArrowDown className="w-3 h-3" />}
                  </span>
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                  <p className="text-sm font-semibold text-gray-600">{stat.title}</p>
                  <p className="text-xs text-gray-400 mt-2">{stat.description}</p>
                </div>
              </div>
            </div>
          ))
        )}

        {/* AI Credits — 4th card, shown only when stats have loaded AND the tenant has a plan.
            The !statsLoading guard prevents it rendering next to the 4 skeleton loaders (5 cards).
            No plan → the Departments card (in the stats array above) fills this slot instead. */}
        {!statsLoading && creditSummary?.has_plan && (() => {
          const remaining = creditSummary.remaining_credits || 0;
          const rollover = creditSummary.rollover_credits || 0;
          const fmt = (n: number) => Math.round(n).toLocaleString();
          const fmtDate = (d?: string) => (d ? format(new Date(d), 'dd MMM yyyy') : '—');
          const planLabel = creditSummary.plan_name || creditSummary.plan_key || 'AI Plan';

          return (
            <div className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-brand-indigo-500/10 to-brand-purple-500/10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-xl bg-brand-indigo-100 text-brand-indigo-700 group-hover:scale-110 transition-transform duration-300">
                    <Wallet className="w-5 h-5" />
                  </div>
                  {creditSummary.is_expired ? (
                    <span className="flex items-center gap-1 text-sm font-bold px-2 py-1 rounded-full bg-red-100 text-red-600">
                      <AlertTriangle className="w-3 h-3" /> Expired
                    </span>
                  ) : creditSummary.auto_renew ? (
                    <span className="flex items-center gap-1 text-sm font-bold px-2 py-1 rounded-full bg-blue-100 text-blue-600">
                      <RefreshCw className="w-3 h-3" /> Auto
                    </span>
                  ) : (
                    <span className="text-sm font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-600">Active</span>
                  )}
                </div>
                <div>
                  <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-400 mb-0.5">Plan Name</p>
                      <p className="text-base font-bold text-gray-800 truncate" title={planLabel}>{planLabel}</p>
                    </div>
                    <div className="text-right shrink-0 max-w-[55%]">
                      <p className="text-xs font-medium text-gray-400 mb-0.5">Remaining Credits</p>
                      <p className="text-2xl font-bold text-gray-900 truncate flex items-center justify-end gap-1" title={fmt(remaining)}>
                        <Coins className="w-4 h-4 text-brand-indigo-500 shrink-0" />
                        {fmt(remaining)}
                      </p>
                    </div>
                  </div>
                  {rollover > 0 && (
                    <p className="text-xs text-emerald-600 font-medium mt-2 truncate">
                      <Coins className="w-3 h-3 inline -mt-0.5" /> +{fmt(rollover)} rolled over
                    </p>
                  )}
                  {(creditSummary.start_date || creditSummary.end_date) && (
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <CalendarClock className="w-3 h-3 shrink-0" />
                      <span className="truncate">{fmtDate(creditSummary.start_date)} → {fmtDate(creditSummary.end_date)}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
      {/* Learning Progress & Certificates Earned */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Learning Progress Tracker */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900">Learning Progress Tracker</h2>
            <BookOpen className="w-5 h-5 text-brand-indigo-600" />
          </div>

          {/* Type toggle pills */}
          <div className="flex gap-2 mb-3">
            {(['courses', 'certifications', 'learningPaths'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setAssignmentChartType(type)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all duration-200 ${
                  assignmentChartType === type
                    ? 'bg-brand-indigo-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {assignmentChartLabels[type]}
              </button>
            ))}
          </div>

          {/* Summary stat pills */}
          {assignmentStatusStats && !assignmentStatusLoading && (() => {
            const breakdown = assignmentStatusStats[assignmentChartType];
            if (!breakdown?.not_started) return null;
            const notStartedTotal = breakdown.not_started.reduce((s: number, b: any) => s + b.value, 0);
            const inProgressTotal = breakdown.in_progress.reduce((s: number, b: any) => s + b.value, 0);
            const completedTotal = breakdown.completed.reduce((s: number, b: any) => s + b.value, 0);

            return (
              <div className="flex gap-4 mb-4">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                  <span className="text-xs text-gray-500">Not Started</span>
                  <span className="text-xs font-bold text-gray-800">{notStartedTotal}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                  <span className="text-xs text-gray-500">In Progress</span>
                  <span className="text-xs font-bold text-gray-800">{inProgressTotal}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                  <span className="text-xs text-gray-500">Completed</span>
                  <span className="text-xs font-bold text-gray-800">{completedTotal}</span>
                </div>
              </div>
            );
          })()}

          {assignmentStatusLoading ? (
            <div className="flex justify-center items-center h-full min-h-[280px]">
              <Loader2 className="w-8 h-8 animate-spin text-brand-indigo-600" />
            </div>
          ) : assignmentChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={assignmentChartData} margin={{ top: 5, right: 15, left: -15, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  height={70}
                  tick={({ x, y, payload }: any) => (
                    <g transform={`translate(${x},${y})`}>
                      <title>{payload.value}</title>
                      <text x={0} y={0} dy={8} textAnchor="end" fill="#9ca3af" fontSize={6} transform="rotate(-45)">
                        {payload.value}
                      </text>
                    </g>
                  )}
                />
                <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div className="bg-white p-3 rounded-lg shadow-xl border border-gray-200 min-w-[180px]">
                      <p className="font-semibold text-gray-900 text-sm mb-2">{d?.label}</p>
                      <div className="space-y-1">
                        <p className="text-xs flex justify-between"><span className="text-amber-600">Not Started</span><span className="font-semibold">{d?.not_started || 0}</span></p>
                        <p className="text-xs flex justify-between"><span className="text-blue-600">In Progress</span><span className="font-semibold">{d?.in_progress || 0}</span></p>
                        <p className="text-xs flex justify-between"><span className="text-emerald-600">Completed</span><span className="font-semibold">{d?.completed || 0}</span></p>
                      </div>
                    </div>
                  );
                }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                <Line type="monotone" dataKey="not_started" name="Not Started" stroke="#f59e0b" strokeWidth={2} dot={assignmentChartData.length <= 15 ? { r: 3, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2 } : false} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }} />
                <Line type="monotone" dataKey="in_progress" name="In Progress" stroke="#3b82f6" strokeWidth={2} dot={assignmentChartData.length <= 15 ? { r: 3, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 } : false} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }} />
                <Line type="monotone" dataKey="completed" name="Completed" stroke="#10b981" strokeWidth={2} dot={assignmentChartData.length <= 15 ? { r: 3, fill: '#10b981', stroke: '#fff', strokeWidth: 2 } : false} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChartState message="No assignment data found for this period." />
          )}
        </div>

        {/* Certificates Earned Chart */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900">Certificates Earned</h2>
            <Award className="w-5 h-5 text-brand-purple-600" />
          </div>

          {/* Summary totals */}
          {certificateEarnedStats && !certificateEarnedLoading && (
            <div className="flex gap-4 mb-4">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#6366f1' }}></span>
                <span className="text-xs text-gray-500">Courses</span>
                <span className="text-xs font-bold text-gray-800">{certificateEarnedStats.totals.courses}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#f59e0b' }}></span>
                <span className="text-xs text-gray-500">Certifications</span>
                <span className="text-xs font-bold text-gray-800">{certificateEarnedStats.totals.certifications}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#10b981' }}></span>
                <span className="text-xs text-gray-500">Learning Paths</span>
                <span className="text-xs font-bold text-gray-800">{certificateEarnedStats.totals.learningPaths}</span>
              </div>
            </div>
          )}

          {certificateEarnedLoading ? (
            <div className="flex justify-center items-center h-full min-h-[300px]">
              <Loader2 className="w-8 h-8 animate-spin text-brand-indigo-600" />
            </div>
          ) : certificateChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={certificateChartData} outerRadius="75%">
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis
                  dataKey="label"
                  tick={({ x, y, payload, textAnchor }: any) => (
                    <g transform={`translate(${x},${y})`}>
                      <title>{payload.value}</title>
                      <text x={0} y={0} textAnchor={textAnchor} fill="#6b7280" fontSize={6}>
                        {payload.value}
                      </text>
                    </g>
                  )}
                />
                <PolarRadiusAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div className="bg-white p-3 rounded-lg shadow-xl border border-gray-200 min-w-[180px]">
                      <p className="font-semibold text-gray-900 text-sm mb-2">{d?.label}</p>
                      <div className="space-y-1">
                        <p className="text-xs flex justify-between"><span className="text-indigo-600">Courses</span><span className="font-semibold">{d?.courses || 0}</span></p>
                        <p className="text-xs flex justify-between"><span className="text-amber-600">Certifications</span><span className="font-semibold">{d?.certifications || 0}</span></p>
                        <p className="text-xs flex justify-between"><span className="text-emerald-600">Learning Paths</span><span className="font-semibold">{d?.learningPaths || 0}</span></p>
                      </div>
                    </div>
                  );
                }} />
                <Legend iconType="diamond" iconSize={10} wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                <Radar name="Courses" dataKey="courses" stroke="#6366f1" fill="#6366f1" fillOpacity={0.1} strokeWidth={1} dot={{ r: 4, fill: '#6366f1', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#6366f1', stroke: '#c7d2fe', strokeWidth: 3 }} />
                <Radar name="Certifications" dataKey="certifications" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} strokeWidth={1} dot={{ r: 4, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#f59e0b', stroke: '#fef3c7', strokeWidth: 3 }} />
                <Radar name="Learning Paths" dataKey="learningPaths" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={1} dot={{ r: 4, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#10b981', stroke: '#d1fae5', strokeWidth: 3 }} />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChartState message="No certificates earned in this period." />
          )}
        </div>
      </div>
      {/* Activity Heatmap */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-900">Activity Heatmap</h2>
            <Activity className="w-5 h-5 text-brand-purple-600" />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            {activityHeatmapData.map((activity, index) => {
              const intensity = activity.percentage / 20; // Scale to 0-5 for opacity
              return (
                <div
                  key={index}
                  className="relative group cursor-pointer transform transition-all duration-300 hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, ${activity.color}${Math.round(intensity * 50).toString(16).padStart(2, '0')}, ${activity.color}20)`
                  }}
                >
                  <div className="p-4 rounded-xl border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl font-bold text-gray-900">{activity.count}</span>
                      <span 
                        className="text-xs font-semibold px-2 py-1 rounded-full"
                        style={{ 
                          backgroundColor: `${activity.color}20`,
                          color: activity.color
                        }}
                      >
                        {activity.percentage.toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 font-medium">{activity.name}</p>
                    <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-700"
                        style={{ 
                          width: `${activity.percentage}%`,
                          backgroundColor: activity.color
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Hover tooltip */}
                  <div className="absolute inset-x-0 -top-12 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 mx-auto w-max">
                      {activity.count.toLocaleString()} events
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      {/* Recent Activity Timeline */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Activity Timeline</h2>
            <p className="text-sm text-gray-500 mt-1">Real-time learning events</p>
          </div>
          <Link
            to={ADMIN_REPORTS_ACTIVITY}
            className="text-sm font-semibold text-brand-indigo-600 hover:text-brand-indigo-700"
            onClick={() => {
              trackEvent(ADMIN_DASHBOARD_EVENTS.NAVIGATION_VIEW_ALL_ACTIVITY, {
                from: '/admin/dashboard',
                to: ADMIN_REPORTS_ACTIVITY,
              });
            }}
          >
            View All Activity →
          </Link>
        </div>

        {activitiesLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex items-start gap-4 animate-pulse">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="w-48 h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="w-full h-3 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-indigo-500 to-brand-purple-500"></div>
            
            <div className="space-y-6">
              {recentActivitiesResponse?.data?.slice(0, 5).map((activity) => {
                const getActivityColor = () => {
                  switch(activity.status) {
                    case ActivityStatus.SUCCESS: return 'bg-green-500';
                    case ActivityStatus.WARNING: return 'bg-yellow-500';
                    case ActivityStatus.ERROR: return 'bg-red-500';
                    default: return 'bg-blue-500';
                  }
                };

                return (
                  <div key={activity._id} className="relative flex items-start gap-4">
                    <div className={`relative z-10 w-10 h-10 ${getActivityColor()} rounded-full flex items-center justify-center shadow-lg`}>
                      <Activity className="w-5 h-5" />
                      <div className="absolute -inset-1 bg-white rounded-full -z-10"></div>
                    </div>
                    
                    <div className="flex-1 bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{activity.action}</p>
                          <p className="text-sm text-gray-600 mt-1">{activity.details}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-gray-500">
                              {activity.metadata?.user_name || 'System'}
                            </span>
                            <span className="text-xs text-gray-400">
                              {format(new Date(activity.timestamp), "dd-MM-yyyy hh:mm a")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
    
